/**
 * lib/companyStamp.ts
 * Draws a round "rubber-stamp" impression for the company using jsPDF
 * vector primitives: a double ring, a star at top, and centred legal
 * name / registration / location. Classic navy stamp ink.
 */
import type { jsPDF } from 'jspdf';
import type { CompanySettings } from '@/types';

const INK: [number, number, number] = [30, 58, 95]; // navy stamp ink

function star(doc: jsPDF, cx: number, cy: number, r: number) {
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < 10; i++) {
    const rad = i % 2 === 0 ? r : r * 0.42;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    pts.push([cx + rad * Math.cos(a), cy + rad * Math.sin(a)]);
  }
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = pts[(i + 1) % pts.length];
    doc.line(p[0], p[1], q[0], q[1]);
  }
}

/** Draw the company stamp centred at (cx, cy) with outer radius r (mm). */
export function drawCompanyStamp(doc: jsPDF, company: CompanySettings, cx: number, cy: number, r = 21) {
  doc.setDrawColor(INK[0], INK[1], INK[2]);
  doc.setTextColor(INK[0], INK[1], INK[2]);
  doc.setLineCap('round');
  doc.setLineJoin('round');

  // Double ring
  doc.setLineWidth(1.1);
  doc.circle(cx, cy, r, 'S');
  doc.setLineWidth(0.4);
  doc.circle(cx, cy, r * 0.82, 'S');

  // Star at top
  star(doc, cx, cy - r * 0.6, r * 0.13);

  // Centred name (wrapped), reg, location
  const name = (company.legalName || company.tradingName || 'Company').toUpperCase();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  const nameLines = doc.splitTextToSize(name, r * 1.5) as string[];
  let ty = cy - (nameLines.length - 1) * 2.2 - 1.5;
  nameLines.forEach((l) => {
    doc.text(l, cx, ty, { align: 'center' });
    ty += 3.1;
  });

  // divider
  doc.setLineWidth(0.3);
  doc.line(cx - r * 0.4, ty - 0.5, cx + r * 0.4, ty - 0.5);
  ty += 2.6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.6);
  if (company.registrationNumber) {
    doc.text(`REG. ${company.registrationNumber}`, cx, ty, { align: 'center' });
    ty += 3;
  }
  doc.text('GABORONE · BOTSWANA', cx, ty, { align: 'center' });
}
